import _ from "lodash";
import React, { useRef, useEffect, useCallback } from "react";
import { useSortable, SortableContext } from "@dnd-kit/sortable";
import classes from "./DraggableRow.scss";
import { DotsSixVerticalOutlined } from "@toddle-edu/ds-icons";
import { CSS } from "@dnd-kit/utilities";
import DraggableCell from "../DraggableCell";
import PropTypes from "prop-types";
import classNames from "classnames";
import { isSafari } from "Utils";

const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};
// Row Component
const DraggableRow = ({
  row,
  columnOrder,
  setCellValue,
  mode,
  hasColHeader,
  rowIndex,
  hasRowHeader,
  setColumnOptionsPosition,
  setShowColumnOptions,
  setShowRowOptions,
  setRowOptionsPosition,
  showRowOptions,
  showColumnOptions,
  getTableActivePrarms,
  additionalClassesRow = [],
  suffixElementRow = [],
  customCellHeaderStyles,
  showDisabledCellClasses,
  shouldHighlightInputText,
  additionalCellStylesRow = [],
  testId,
  lastRowIndex,
  customActiveCellStyles,
}) => {
  const rowId = _.get(row, "id");

  const {
    transform,
    transition,
    setNodeRef,
    isDragging,
    attributes,
    listeners,
  } = useSortable({
    id: rowId,
  });

  // reference for drag handle, to get click event
  const dragHandleRef = useRef();
  const rowRef = useRef();

  useEffect(() => {
    if (!transform) return;
    !_.inRange(_.get(transform, "x"), -2, 2) ||
    !_.inRange(_.get(transform, "y"), -2, 2)
      ? setShowRowOptions(-1)
      : null;
  }, [transform]);

  useEffect(() => {
    let isClicked = true;
    // starting of click
    function mouseDown(e) {
      isClicked = true;
    }
    // if mouse moves, it is drag so clicked is false
    function mouseMove(e) {
      isClicked = false;
    }
    // if mouse clicked then only show Row Options Overlay
    function mouseUp(e) {
      if (isClicked) {
        // setting rowOptions to id of row to know which row to show for row options overlay
        setShowRowOptions(rowId);
        if (dragHandleRef?.current) {
          const rect = dragHandleRef?.current?.getBoundingClientRect();
          // getting the row position in window, to set row options overlay
          setRowOptionsPosition({
            top: rect?.top,
            left: rect?.left,
            width: rect?.width,
            height: rect?.height,
            rowIndex: rowIndex,
            rowId: rowId,
          });
        }
      }

      // Reset this back to false for next time
      isClicked = false;
    }

    // // reposition overlay on window resize
    function repositionOverlay(e) {
      if (dragHandleRef?.current && _.isEqual(showRowOptions, rowId)) {
        const rect = dragHandleRef?.current?.getBoundingClientRect();
        // getting the column position in window, to set column options overlay
        setRowOptionsPosition({
          top: rect?.top,
          left: rect?.left,
          width: rect?.width,
          height: rect?.height,
          rowIndex: rowIndex,
        });
      }
    }

    if (dragHandleRef && dragHandleRef?.current) {
      dragHandleRef?.current?.addEventListener("mousedown", mouseDown);
      dragHandleRef?.current?.addEventListener("mousemove", mouseMove);
      dragHandleRef?.current?.addEventListener("mouseup", mouseUp);
      window.addEventListener("resize", repositionOverlay);
      window.addEventListener("wheel", repositionOverlay);

      return () => {
        // cleanup functions
        if (dragHandleRef && dragHandleRef?.current) {
          dragHandleRef?.current?.removeEventListener("mousedown", mouseDown);
          dragHandleRef?.current?.removeEventListener("mouseup", mouseUp);
          dragHandleRef?.current?.removeEventListener("mousemove", mouseMove);
          window.removeEventListener("resize", repositionOverlay);
          window.removeEventListener("wheel", repositionOverlay);
        }
      };
    }
  }, [dragHandleRef?.current, showRowOptions]);

  const style = {
    transform: CSS.Translate.toString(transform), //let dnd-kit do its thing
    transition: transition,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
    ...(isDragging && { backgroundColor: "var(--surface-primary-enabled)" }),
  };

  const tableRowClasses = classNames(classes.tableRow, {
    [classes.rowActive]: _.isEqual(rowId, showRowOptions),
    [classes.lastRowActive]:
      _.isEqual(rowId, showRowOptions) && _.isEqual(rowIndex, lastRowIndex),
  });

  const onMouseEnter = useCallback(() => {
    if (_.isEqual(mode, "create")) {
      _.set(dragHandleRef.current, "style.visibility", "visible");
      if (isSafari())
        _.set(
          dragHandleRef.current,
          "style.cssText",
          `visibility:visible;top: ${
            _.get(rowRef, ["current", "clientHeight"], 36) / 2 - 10
          }px;`
        );
    }
  }, []);

  const onMouseLeave = useCallback(() => {
    if (_.isEqual(mode, "create"))
      if (_.isEqual(showRowOptions, rowId)) {
        _.set(dragHandleRef.current, "style.visibility", "visible");
        if (isSafari())
          _.set(
            dragHandleRef.current,
            "style.cssText",
            "visibility:visible;top:8px;"
          );
      } else {
        _.set(dragHandleRef.current, "style.visibility", "hidden");
      }
  }, [showRowOptions]);

  useEffect(() => {
    if (!_.isEqual(showRowOptions, rowId)) {
      _.set(dragHandleRef.current, "style.visibility", "hidden");
    }
  }, [showRowOptions]);

  const dragHandleClasses = classNames({
    [classes.hide]: !_.get(row, "original.showToolbar", true),
  });

  return (
    <tr
      ref={node => {
        if (_.isNil(node)) return;
        setNodeRef(node);
        rowRef.current = node;
      }}
      style={style}
      className={tableRowClasses}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {row.getVisibleCells().map((cell, colIndex) => {
        const colId = _.get(cell, ["column", "columnDef", "accessorKey"]);
        const additionalClassesCell = _.get(
          additionalClassesRow,
          colId,
          EMPTY_ARRAY
        );
        const additionalCellStyles = _.get(
          additionalCellStylesRow,
          colId,
          EMPTY_OBJ
        );
        const suffixElementCell = _.get(suffixElementRow, colId, null);
        return (
          <SortableContext key={cell.id} items={columnOrder}>
            <DraggableCell
              key={cell.id}
              cell={cell}
              columnId={_.get(cell, ["column", "id"])}
              setCellValue={setCellValue}
              mode={mode}
              hasColHeader={hasColHeader}
              colIndex={colIndex}
              rowIndex={rowIndex}
              hasRowHeader={hasRowHeader}
              setColumnOptionsPosition={setColumnOptionsPosition}
              setShowColumnOptions={setShowColumnOptions}
              setShowRowOptions={setShowRowOptions}
              showColumnOptions={showColumnOptions}
              getTableActivePrarms={getTableActivePrarms}
              additionalClassesCell={additionalClassesCell}
              suffixElementCell={suffixElementCell}
              rowActive={_.isEqual(rowId, showRowOptions)}
              isHoverOnRow={_.isEqual(
                _.get(dragHandleRef.current, "style.visibility", ""),
                "visible"
              )}
              customCellHeaderStyles={customCellHeaderStyles}
              showDisabledCellClasses={showDisabledCellClasses}
              shouldHighlightInputText={shouldHighlightInputText}
              additionalCellStyles={additionalCellStyles}
              testId={testId}
              customActiveCellStyles={customActiveCellStyles}
            />
          </SortableContext>
        );
      })}
      {_.isEqual(mode, "create") ? (
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          dragHandleRef={dragHandleRef}
          testId={testId}
          dragHandleClasses={dragHandleClasses}
        />
      ) : null}
    </tr>
  );
};
const areEqual = (prevProps, nextProps) => {
  return _.isEqual(
    _.omitBy(prevProps, (val, key) => {
      return (
        (typeof val === "function" &&
          !_.includes(["setCellValue", "getTableActivePrarms"], key)) ||
        key === "listeners"
      );
    }),
    _.omitBy(nextProps, (val, key) => {
      return (
        (typeof val === "function" &&
          !_.includes(["setCellValue", "getTableActivePrarms"], key)) ||
        key === "listeners"
      );
    })
  );
};
const DragHandle = React.memo(
  ({ attributes, listeners, dragHandleRef, testId, dragHandleClasses }) => {
    return (
      <span
        // ref to detect click on drag handle
        ref={node => (dragHandleRef.current = node)}
        {...attributes}
        {...listeners}
        data-test-id={`${testId}-draggableRow-rowHandle`}
        className={dragHandleClasses}
      >
        <DotsSixVerticalOutlined />
      </span>
    );
  },
  areEqual
);
DragHandle.displayName = "DragHandle";

DraggableRow.propTypes = {
  row: PropTypes.object,
  columnOrder: PropTypes.array,
  setCellValue: PropTypes.func,
  mode: PropTypes.string,
  hasColHeader: PropTypes.bool,
  rowIndex: PropTypes.number,
  hasRowHeader: PropTypes.bool,
  showEvaluatedQuestion: PropTypes.bool,
  testId: PropTypes.string,
};

export default React.memo(DraggableRow, areEqual);
